import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { FiArrowLeft, FiUserCheck, FiShield, FiBookOpen, FiSettings, FiUsers, FiFileText, FiClock, FiTrendingUp } = FiIcons;

const OnboardingWorkflow = () => {
  const [selectedPhase, setSelectedPhase] = useState('prestart');

  const onboardingPhases = {
    prestart: {
      title: 'Pre-Start Preparation',
      subtitle: 'Essential setup before day one',
      timeline: '1-2 weeks before start',
      phases: [
        {
          phase: 'System Setup & Documentation',
          urgency: 'critical',
          icon: FiSettings,
          actions: [
            '**Create employee record** in HR system with unique identifier',
            'Set up **IT accounts and equipment** (email, systems access)',
            'Prepare **employee handbook** and key policy documents',
            'Generate **building access cards** and parking permits',
            'Schedule **induction calendar** with key stakeholders'
          ],
          compliance: [
            { type: 'legal', text: 'GDPR: Employee data must be processed lawfully and securely' },
            { type: 'acas', text: 'Well-prepared first day improves retention and engagement' }
          ]
        },
        {
          phase: 'Workspace & Equipment Preparation',
          urgency: 'high',
          icon: FiShield,
          actions: [
            'Prepare **workstation and safety equipment**',
            'Conduct **DSE assessment** if computer-based role',
            'Arrange **health and safety briefing** materials',
            'Prepare **emergency evacuation** information',
            'Set up **buddy/mentor** assignment'
          ],
          compliance: [
            { type: 'legal', text: 'Health and Safety at Work Act 1974: Duty to provide safe workplace' },
            { type: 'legal', text: 'DSE Regulations 1992: Assessment required for display screen work' }
          ]
        }
      ]
    },
    dayone: {
      title: 'Day One Induction',
      subtitle: 'Critical first day activities',
      timeline: 'Day 1',
      phases: [
        {
          phase: 'Welcome & Legal Requirements',
          urgency: 'critical',
          icon: FiUserCheck,
          actions: [
            '**Personal welcome** by line manager and HR',
            'Complete **right to work verification** if not done pre-start',
            'Issue **written statement of employment** (contract signing)',
            'Provide **employee handbook** and key policies',
            'Complete **emergency contact** and next of kin details'
          ],
          compliance: [
            { type: 'legal', text: 'Employment Rights Act 1996: Written statement must be provided by day 1' },
            { type: 'legal', text: 'Immigration Act 2016: Right to work checks before work commences' }
          ]
        },
        {
          phase: 'Health & Safety Induction',
          urgency: 'critical',
          icon: FiShield,
          actions: [
            'Conduct **mandatory health and safety induction**',
            'Explain **fire evacuation procedures** and assembly points',
            'Identify **first aiders and safety representatives**',
            'Complete **risk assessment** for role-specific hazards',
            'Issue **PPE and safety equipment** if required'
          ],
          compliance: [
            { type: 'legal', text: 'Health and Safety at Work Act 1974: Mandatory safety induction' },
            { type: 'legal', text: 'Management of Health and Safety at Work Regulations 1999' }
          ]
        },
        {
          phase: 'Essential Information & Systems',
          urgency: 'high',
          icon: FiBookOpen,
          actions: [
            'Provide **organizational structure** and reporting lines',
            'Explain **working time arrangements** (hours, breaks, overtime)',
            'Set up **IT systems access** and security training',
            'Introduce **key policies**: code of conduct, grievance, disciplinary',
            'Complete **data protection training** and consent forms'
          ],
          compliance: [
            { type: 'legal', text: 'Working Time Regulations 1998: Must inform employees of rights' },
            { type: 'legal', text: 'GDPR: Data protection training required for all staff' }
          ]
        }
      ]
    },
    firstweek: {
      title: 'First Week Integration',
      subtitle: 'Building foundations for success',
      timeline: 'Days 2-7',
      phases: [
        {
          phase: 'Role-Specific Training',
          urgency: 'high',
          icon: FiTrendingUp,
          actions: [
            'Conduct **detailed job role briefing** with line manager',
            'Provide **role-specific training** and competency requirements',
            'Introduce **key stakeholders** and internal customers',
            'Explain **performance standards** and measurement criteria',
            'Set **initial objectives** and development priorities'
          ],
          compliance: [
            { type: 'acas', text: 'Clear role expectations reduce performance issues' },
            { type: 'legal', text: 'Reasonable training required under implied duty of care' }
          ]
        },
        {
          phase: 'Policy Acknowledgment & Training',
          urgency: 'high',
          icon: FiFileText,
          actions: [
            'Complete **equality and diversity training**',
            'Review **anti-harassment and bullying policies**',
            'Understand **grievance and disciplinary procedures**',
            'Complete **confidentiality and data protection** acknowledgments',
            '**Sign policy acknowledgment forms** for all key policies'
          ],
          compliance: [
            { type: 'legal', text: 'Equality Act 2010: Employers must take steps to prevent discrimination' },
            { type: 'legal', text: 'GDPR Article 32: Staff training on data security required' }
          ]
        },
        {
          phase: 'Benefits & Entitlements Setup',
          urgency: 'medium',
          icon: FiUsers,
          actions: [
            'Enroll in **workplace pension scheme** (auto-enrolment)',
            'Explain **holiday entitlement** and booking procedures',
            'Set up **payroll arrangements** and payment methods',
            'Provide **employee benefits** information and enrollment',
            'Complete **tax code verification** and P45 processing'
          ],
          compliance: [
            { type: 'legal', text: 'Pensions Act 2008: Auto-enrolment within 3 months of employment' },
            { type: 'legal', text: 'Working Time Regulations 1998: Minimum 5.6 weeks annual leave' }
          ]
        }
      ]
    },
    probation: {
      title: 'Probationary Period Management',
      subtitle: 'Ongoing support and assessment',
      timeline: '3-6 months',
      phases: [
        {
          phase: 'Probation Framework Setup',
          urgency: 'high',
          icon: FiClock,
          actions: [
            'Establish **probationary period length** (typically 3-6 months)',
            'Set **clear performance criteria** and success measures',
            'Schedule **regular review meetings** (weekly initially, then monthly)',
            'Assign **mentor or buddy** for ongoing support',
            'Document **probation objectives** and development plan'
          ],
          compliance: [
            { type: 'acas', text: 'Clear probation criteria and support improve success rates' },
            { type: 'legal', text: 'Fair probation process reduces unfair dismissal risk' }
          ]
        },
        {
          phase: 'Ongoing Development & Support',
          urgency: 'medium',
          icon: FiTrendingUp,
          actions: [
            'Provide **continuous training** and skill development',
            'Conduct **regular feedback sessions** and coaching',
            'Monitor **integration with team** and cultural fit',
            'Address **performance concerns** early with support',
            'Document **progress against objectives** throughout period'
          ],
          compliance: [
            { type: 'acas', text: 'Early intervention prevents performance issues escalating' },
            { type: 'legal', text: 'Reasonable support required during probation period' }
          ]
        },
        {
          phase: 'Probation Review & Confirmation',
          urgency: 'critical',
          icon: FiUserCheck,
          actions: [
            'Conduct **formal probation review meeting**',
            'Assess **performance against agreed criteria**',
            'Make **confirmation or extension decision**',
            'If unsuccessful: follow **fair dismissal process**',
            '**Document decision rationale** and communicate outcome'
          ],
          compliance: [
            { type: 'legal', text: 'Fair process required even during probation (less than 2 years service)' },
            { type: 'acas', text: 'Clear decision rationale and communication essential' }
          ]
        }
      ]
    },
    longterm: {
      title: 'Long-term Integration',
      subtitle: 'Sustained engagement and development',
      timeline: '6+ months',
      phases: [
        {
          phase: 'Performance Management Integration',
          urgency: 'medium',
          icon: FiTrendingUp,
          actions: [
            'Transition to **regular performance management** cycle',
            'Set **annual objectives** aligned with business goals',
            'Establish **development planning** and career progression',
            'Integrate into **team projects** and decision-making',
            'Begin **cross-training** and skill diversification'
          ],
          compliance: [
            { type: 'acas', text: 'Ongoing performance management improves retention' },
            { type: 'legal', text: 'Reasonable training and development opportunities' }
          ]
        },
        {
          phase: 'Culture & Engagement',
          urgency: 'low',
          icon: FiUsers,
          actions: [
            'Encourage **participation in company initiatives**',
            'Provide **networking opportunities** within organization',
            'Seek **feedback on induction experience** for improvement',
            'Monitor **engagement levels** and job satisfaction',
            'Plan **career development discussions** and progression'
          ],
          compliance: [
            { type: 'acas', text: 'Employee engagement reduces turnover and improves performance' }
          ]
        }
      ]
    }
  };

  const currentPhase = onboardingPhases[selectedPhase];

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-600';
      case 'high': return 'bg-orange-600';
      case 'medium': return 'bg-yellow-600';
      case 'low': return 'bg-green-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/" className="mr-4">
          <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Onboarding / Induction Workflow</h1>
          <p className="text-gray-600">Comprehensive employee onboarding ensuring legal compliance and engagement</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.keys(onboardingPhases).map((phase) => (
            <button
              key={phase}
              onClick={() => setSelectedPhase(phase)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedPhase === phase 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {phase === 'prestart' ? 'Pre-Start' : 
               phase === 'dayone' ? 'Day One' :
               phase === 'firstweek' ? 'First Week' :
               phase === 'probation' ? 'Probation' : 'Long-term'}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentPhase.title}</h2>
            <p className="text-gray-600">{currentPhase.subtitle}</p>
          </div>
          <div className="text-right">
            <span className="text-sm text-green-600 font-medium">Timeline</span>
            <p className="text-lg font-bold text-green-800">{currentPhase.timeline}</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {currentPhase.phases.map((phase, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className={`${getUrgencyColor(phase.urgency)} px-6 py-4`}>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={phase.icon} className="text-white text-2xl" />
                <div>
                  <h3 className="text-white font-semibold text-xl">{phase.phase}</h3>
                  <span className="text-white opacity-90 text-sm capitalize">
                    {phase.urgency} Priority
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiUserCheck} className="text-green-600 mr-2" />
                    Required Actions
                  </h4>
                  <ul className="space-y-3">
                    {phase.actions.map((action, actionIndex) => (
                      <li key={actionIndex} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                        <span 
                          className="text-gray-700"
                          dangerouslySetInnerHTML={{ 
                            __html: action.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <SafeIcon icon={FiShield} className="text-orange-600 mr-2" />
                    Compliance Requirements
                  </h4>
                  <div className="space-y-3">
                    {phase.compliance.map((requirement, reqIndex) => (
                      <div key={reqIndex} className="flex items-start space-x-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded flex-shrink-0 ${
                          requirement.type === 'legal' 
                            ? 'text-red-600 bg-red-50' 
                            : 'text-green-600 bg-green-50'
                        }`}>
                          {requirement.type === 'legal' ? '⚖️ Legal' : '🟢 ACAS'}
                        </span>
                        <span className="text-gray-700 text-sm">{requirement.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h4 className="font-semibold text-red-900 mb-3">⚖️ Legal Compliance Checklist</h4>
          <ul className="text-sm text-red-800 space-y-2">
            <li>✓ Written employment terms (Day 1)</li>
            <li>✓ Health & safety induction (mandatory)</li>
            <li>✓ Right to work verification</li>
            <li>✓ Auto-enrolment pension setup</li>
            <li>✓ GDPR data protection training</li>
            <li>✓ Working time regulations briefing</li>
          </ul>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h4 className="font-semibold text-green-900 mb-3">🟢 Best Practice Benefits</h4>
          <ul className="text-sm text-green-800 space-y-2">
            <li>• Improved employee retention (65% increase)</li>
            <li>• Faster time to productivity (30% faster)</li>
            <li>• Enhanced engagement and satisfaction</li>
            <li>• Reduced early turnover costs</li>
            <li>• Better cultural integration</li>
            <li>• Stronger performance outcomes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWorkflow;